import { Fragment, useEffect, useId, useRef, useState, KeyboardEvent, FocusEvent } from 'react';
import { css, styled } from 'styled-components';
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form';
import { Icon } from '@/core/components';

export interface SelectOption {
	id: number;
	title: string;
}

export interface SelectFieldProps<TFieldValues extends FieldValues = FieldValues> {
	placeholder?: string;
	dataQaId?: string;
	name: FieldPath<TFieldValues>;
	control: Control<TFieldValues>;
	options: SelectOption[];
	disabled?: boolean;
	className?: string;
	required?: boolean;
}

const StyledSelectWrapper = styled.div<{ $hasError: boolean }>`
	position: relative;
	width: 100%;
	margin: 8px 0;
`;

const StyledSelectButton = styled.button<{ $isOpen: boolean; $hasError: boolean }>`
	outline: none;
	padding: 16px 48px 16px 16px;
	cursor: pointer;
	position: relative;
	background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%23656565' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 18px center;
	background-size: auto;
	background-color: #fff;
	border: 1px solid ${(p) => (p.$hasError ? css`var(--Red-80)` : css`var(--Grey-40)`)};
	border-radius: 12px;
	box-sizing: border-box;
	width: 100%;
	height: 56px;
	min-height: 56px;
	font-size: 16px;
	font-weight: 400;
	text-align: left;
	line-height: 24px;
	color: var(--Primary-squla-dark-blue);
	transition: background-color 0.2s ease;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}

	&:focus {
		border: 1px solid var(--Primary-squla-blue);
	}
`;

const StyledDropdown = styled.div<{ $isOpen: boolean }>`
	display: ${(p) => (p.$isOpen ? 'flex' : 'none')};
	flex-direction: column;
	padding: 16px 0;
	border-radius: 12px;
	overflow: hidden;
	background-color: white;
	box-shadow:
		0 8px 12px 0 rgba(0, 0, 0, 0.1),
		0 2px 2px 0 rgba(0, 0, 0, 0.08);
	position: absolute;
	left: 0;
	right: 0;
	z-index: 999;
	margin-top: 16px;
	max-height: 260px;
	overflow-y: auto;

	/* Custom scrollbar styles for WebKit browsers */
	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: #f1f1f1;
	}

	&::-webkit-scrollbar-thumb {
		background: #888;
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: #555;
	}

	/* Custom scrollbar styles for Firefox */
	scrollbar-width: thin;
	scrollbar-color: #888 #f1f1f1;
`;

const StyledOption = styled.div<{ $isSelected: boolean }>`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	user-select: none;
	padding: 16px;
	cursor: pointer;
	border-bottom: 1px solid #d3d3d3;
	transition: background-color 0.2s ease;

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background-color: #ebebeb;
	}
`;

const StyledRadioVisual = styled.span<{ $isSelected: boolean }>`
	position: relative;
	width: 20px;
	height: 20px;
	border: 2px solid ${(p) => (p.$isSelected ? 'var(--Primary-squla-blue)' : '#d3d3d3')};
	border-radius: 50%;
	flex-shrink: 0;
	transition: border-color 0.2s ease;

	&::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background-color: var(--Primary-squla-blue);
		opacity: ${(p) => (p.$isSelected ? 1 : 0)};
		transition: opacity 0.2s ease;
	}
`;

const StyledOptionText = styled.span`
	margin-left: 16px;
	color: var(--Primary-squla-dark-blue);
	font-size: 16px;
	line-height: 24px;
`;

const StyledHiddenInput = styled.input`
	position: absolute;
	opacity: 0;
	pointer-events: none;
`;

const StyledErrorMessage = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	color: var(--Red-80);
	font-size: 12px;
`;

export function SelectField<TFieldValues extends FieldValues = FieldValues>(
	props: SelectFieldProps<TFieldValues>,
) {
	const { placeholder, dataQaId, control, name, options, disabled, className, required } = props;
	const [isOpen, setIsOpen] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const optionsRefs = useRef<(HTMLDivElement | null)[]>([]);
	const selectButtonRef = useRef<HTMLButtonElement>(null);
	const { field, fieldState } = useController<TFieldValues>({ control, name });

	const selectedOption = options.find((opt) => opt.id === field.value);
	const displayText = selectedOption
		? selectedOption.title.charAt(0).toUpperCase() + selectedOption.title.slice(1)
		: placeholder;

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	// Focus the option when focusedIndex changes
	useEffect(() => {
		if (isOpen && focusedIndex !== null && optionsRefs.current[focusedIndex]) {
			optionsRefs.current[focusedIndex]?.focus();
		}
	}, [focusedIndex, isOpen]);

	const handleToggle = () => {
		if (!disabled) {
			if (!isOpen) {
				const selectedIdx = options.findIndex((opt) => opt.id === field.value);
				setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0);
			} else {
				setFocusedIndex(null);
			}
			setIsOpen(!isOpen);
		}
	};

	const handleSelect = (optionId: number) => {
		field.onChange(optionId);
		setIsOpen(false);
		setFocusedIndex(null);
		selectButtonRef.current?.focus();
	};

	// Only mark the field as touched when focus leaves the whole component
	// (e.g. tabbing away or clicking outside), not when focus moves between
	// the button and its options while the dropdown is open.
	const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
		if (!wrapperRef.current?.contains(event.relatedTarget as Node)) {
			field.onBlur();
		}
	};

	const handleButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'Escape') {
			setIsOpen(false);
			setFocusedIndex(null);
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			if (!isOpen) {
				const selectedIdx = options.findIndex((opt) => opt.id === field.value);
				setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0);
			} else {
				setFocusedIndex(null);
			}
			setIsOpen((prev) => !prev);
		} else if (event.key === 'ArrowDown' && !isOpen) {
			event.preventDefault();
			const selectedIdx = options.findIndex((opt) => opt.id === field.value);
			setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0);
			setIsOpen(true);
		}
	};

	const handleOptionKeyDown = (event: KeyboardEvent<HTMLDivElement>, idx: number) => {
		if (event.key === 'Escape') {
			setIsOpen(false);
			setFocusedIndex(null);
		} else if (event.key === 'ArrowDown') {
			event.preventDefault();
			setFocusedIndex((prev) => {
				if (prev === null) return 0;
				return Math.min(options.length - 1, prev + 1);
			});
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setFocusedIndex((prev) => {
				if (prev === null) return 0;
				return Math.max(0, prev - 1);
			});
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleSelect(options[idx].id);
		} else if (event.key === 'Tab') {
			setIsOpen(false);
			setFocusedIndex(null);
		}
	};

	const selectId = useId();
	const errorId = `${selectId}-error`;
	const listboxId = `${selectId}-listbox`;
	const focusedOption = isOpen && focusedIndex !== null ? options[focusedIndex] : undefined;
	const activeDescendantId =
		focusedOption?.id !== undefined ? `${selectId}-option-${focusedOption.id}` : undefined;

	return (
		<Fragment>
			<StyledSelectWrapper
				ref={wrapperRef}
				className={className}
				$hasError={fieldState.invalid}
				onBlur={handleWrapperBlur}
			>
				<StyledHiddenInput
					{...field}
					id={selectId}
					type="hidden"
					aria-invalid={fieldState.invalid ? 'true' : 'false'}
					aria-describedby={fieldState.error ? errorId : undefined}
					required={required}
				/>
				<StyledSelectButton
					ref={selectButtonRef}
					type="button"
					onClick={handleToggle}
					onKeyDown={handleButtonKeyDown}
					disabled={disabled}
					data-qa-id={dataQaId}
					aria-haspopup="listbox"
					aria-controls={listboxId}
					aria-expanded={isOpen}
					aria-label={placeholder}
					aria-activedescendant={activeDescendantId}
					aria-describedby={fieldState.error ? errorId : undefined}
					$isOpen={isOpen}
					$hasError={fieldState.invalid}
				>
					{displayText}
				</StyledSelectButton>
				<StyledDropdown
					id={listboxId}
					$isOpen={isOpen}
					role="listbox"
					aria-label={placeholder}
					aria-activedescendant={activeDescendantId}
				>
					{options.map((option, idx) => {
						const isSelected = option.id === field.value;
						const isFocused = focusedIndex === idx;
						return (
							<StyledOption
								key={option.id}
								id={`${selectId}-option-${option.id}`}
								ref={el => { optionsRefs.current[idx] = el; }}
								tabIndex={isOpen && isFocused ? 0 : -1}
								onClick={() => {
									handleSelect(option.id);
								}}
								onKeyDown={(e) => { handleOptionKeyDown(e, idx); }}
								data-qa-id={dataQaId ? `${dataQaId}-item-${option.id}` : undefined}
								role="option"
								aria-selected={isSelected}
								$isSelected={isSelected}
							>
								<StyledRadioVisual $isSelected={isSelected} />
								<StyledOptionText>{option.title}</StyledOptionText>
							</StyledOption>
						);
					})}
				</StyledDropdown>
			</StyledSelectWrapper>
			{fieldState.error && (
				<StyledErrorMessage id={errorId} role="alert">
					<Icon name="alert-triangle" aria-hidden="true" size={16} />
					<span>{fieldState.error.message}</span>
				</StyledErrorMessage>
			)}
		</Fragment>
	);
}

export default SelectField;
